import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseCard from "../../../components/ui/BaseCard";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseSelect from "../../../components/ui/BaseSelect";
import { showToast } from "../../../store/toast/toastSlice";
import { fetchWorkOrderPaymetDetails } from "../../../services/api/workOrder";
import { sendCustomerCounterOffer, updateCustomerCounterOffer } from "../../../services/api/offers";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { CurrencySymbol } from "../../../utils/enum";
import { formatExpenseLabel } from "../../../utils/helpers";
import { getTimeZoneAbbreviation } from "../../../utils/getTimeZoneAbbreviation";
import "../CounterOfferModal/style.css";
import {
  PAYMENT_TYPE_LABELS,
  RATE_FIELDS,
  isRateInputChanged,
  isRateInputValid,
  isRateShown,
  estimateLabour,
  fmt,
  toCustomerRateField,
  toNum,
} from "./customerRates";

const resetExpense = {
  expenseType: "",
  description: "",
  amount: "",
  addedBy: "CLIENT",
  isApproved: false,
};

// Client-side counter offer on a customer-raised ticket (PENDING_APPROVAL): the
// client proposes different customer rates back to the customer before publishing.
// Laid out like the engineer CounterOfferModal, but on customer rates.
// With `existingOffer` (the pending counter offer) it edits that proposal instead:
// the form starts from the proposed rates and expenses and saves over it.
const CustomerCounterOfferModal = ({ open, close, existingOffer = null }) => {
  const isEditing = !!existingOffer;
  const dispatch = useDispatch();
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);
  const paymentType = workOrderDetail?.paymentType;
  const fields = RATE_FIELDS[paymentType] || [];

  const [isLabourOpen, setIsLabourOpen] = useState(false);
  const [isExpensesOpen, setIsExpensesOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState(resetExpense);
  const [paymentForm, setPaymentForm] = useState({});
  const [newPayment, setNewPayment] = useState({});
  const [isEdited, setIsEdited] = useState(false);
  const queryClient = useQueryClient();
  const { updateWorkOrder } = useUpdateWorkOrder();

  const { isPending: isSendingCounterOffer, mutateAsync: sendCustomerCounterOfferMutation } =
    useMutation({
      mutationFn: (payload) =>
        isEditing
          ? updateCustomerCounterOffer(existingOffer.id, payload)
          : sendCustomerCounterOffer(payload),
    });

  const { data: workOrderPaymentData } = useQuery({
    queryKey: ["workOrderPayment", workOrderDetail?.id],
    queryFn: () => fetchWorkOrderPaymetDetails(workOrderDetail.id),
    enabled: !!workOrderDetail?.id,
  });

  // The ticket's own rates are the fallback until the payment breakdown loads.
  const currentRates = { ...workOrderDetail, ...workOrderPaymentData?.data?.clientRates };
  const customerCurrency = CurrencySymbol[currentRates?.currency] || "";

  useEffect(() => {
    const approvedExpenses = (workOrderPaymentData?.data?.clientRates?.expenses || []).map(
      (expense) => ({ ...expense, isApproved: true }),
    );
    // When editing, the sent proposal is the starting point: its expenses are still
    // unapproved (so they can be removed) and its rates fill the form.
    const proposedExpenses = (existingOffer?.expenses || []).map((expense) => ({
      ...expense,
      isApproved: false,
    }));
    setExpenses([...approvedExpenses, ...proposedExpenses]);
    const proposed = Object.fromEntries(
      fields.map(({ name }) => [
        name,
        existingOffer?.[toCustomerRateField(name)] ?? currentRates?.[name] ?? "",
      ]),
    );
    setPaymentForm(proposed);
    if (existingOffer) {
      const changedRates = Object.fromEntries(
        fields
          .filter((field) =>
            isRateInputChanged(field, proposed[field.name], currentRates?.[field.name]),
          )
          .map(({ name }) => [name, toNum(proposed[name])]),
      );
      setNewPayment(changedRates);
      setIsEdited(Object.keys(changedRates).length > 0 || proposedExpenses.length > 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderPaymentData]);

  const proposedRates = { ...currentRates, ...newPayment };
  const addedExpensesTotal = expenses
    .filter((expense) => !expense.isApproved)
    .reduce((sum, expense) => sum + (toNum(expense.amount) || 0), 0);
  const currentTotal = toNum(currentRates?.totalWorkOrderCost) || 0;
  const totalNewWorkOrderCost =
    currentTotal -
    estimateLabour(paymentType, currentRates) +
    estimateLabour(paymentType, proposedRates) +
    addedExpensesTotal;

  const handleLabourChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleExpenseChange = (e) => {
    const { name, value } = e.target;
    setNewExpense((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleAddPay = () => {
    const invalidField = fields.find(
      (field) => !isRateInputValid(field, paymentForm?.[field.name]),
    );
    if (invalidField) {
      dispatch(
        showToast({
          message: "Please enter Labour Values",
          type: "error",
        }),
      );
      return;
    }

    // Only keep the rates that actually differ, so unchanged ones aren't struck through.
    const changedRates = Object.fromEntries(
      fields
        .filter((field) =>
          isRateInputChanged(field, paymentForm[field.name], currentRates?.[field.name]),
        )
        .map(({ name }) => [name, toNum(paymentForm[name])]),
    );
    setNewPayment(changedRates);
    setIsEdited(Object.keys(changedRates).length > 0 || addedExpensesTotal > 0);
    // Added: the summary above now carries the rates, so the inputs start blank.
    setPaymentForm(Object.fromEntries(fields.map(({ name }) => [name, ""])));
  };

  const handleAddExpenseButton = () => {
    // The description is optional, as on every other expense form.
    if (newExpense.expenseType === "" || !(toNum(newExpense.amount) > 0)) {
      dispatch(
        showToast({
          message: "Please enter Expense Values",
          type: "error",
        }),
      );
      return;
    }

    setExpenses((prevItems) => [
      ...prevItems,
      { ...newExpense, amount: toNum(newExpense.amount), isApproved: false },
    ]);
    setIsEdited(true);
    setNewExpense(resetExpense);
  };

  const handleDeleteExpenseButton = (index) => {
    const remaining = expenses.filter((_, i) => i !== index);
    setExpenses(remaining);
    setIsEdited(
      Object.keys(newPayment).length > 0 || remaining.some((expense) => !expense.isApproved),
    );
  };

  const handleCounterOfferButton = async () => {
    // Only the changed rates are sent: the API keeps the work order's rate for any
    // field left out. The expenses are the ones added here, not the existing ones.
    const payload = {
      // An edit is addressed by the offer id, so it carries no work order id.
      ...(isEditing ? {} : { workOrderId: workOrderDetail?.id }),
      ...newPayment,
      expenses: expenses
        .filter((expense) => !expense.isApproved)
        .map(({ expenseType, description, amount }) => ({
          expenseType,
          description,
          amount: toNum(amount),
        })),
    };

    try {
      await sendCustomerCounterOfferMutation(payload);
      dispatch(
        showToast({
          message: isEditing ? "Counter Offer Updated" : "Counter Offer Sent Successfully",
          type: "success",
        }),
      );
      queryClient.invalidateQueries({ queryKey: ["workOrderPayment"] });
      queryClient.invalidateQueries({ queryKey: ["customerCounterOffer"] });
      close(false);
      await updateWorkOrder();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
        }),
      );
    }
  };

  const renderRateValue = (isAmount, value) =>
    isAmount ? `${customerCurrency} ${fmt(value)}` : `${value ?? 0} hrs`;

  return (
    <BaseModal open={open} close={close}>
      <div className="container bg-light xl:px-10">
        <h5 className="text-dark border-bottom mb-3">
          {isEditing ? "Edit Counter Offer" : "Counter Offer"}
        </h5>

        <BaseCard className="p-4 mt-6" elevation={0}>
          <div className="flex justify-between border-bottom mb-2">
            <h6 className="text-dark">Schedule</h6>
            <div className="flex items-center bg-[#F2FAFF] py-1 px-4 rounded-md mb-2">
              <i className="fa-solid fa-circle-exclamation text-primary"></i>
              <p className=" ml-3 font-normal">Fixed Start</p>
            </div>
          </div>
          <div className="flex justify-between">
            <p>Start at a specific date and time</p>
            {workOrderDetail?.orderStartDate ? (
              <p>
                {dayjs.utc(workOrderDetail.orderStartDate).format("dddd, MM/DD/YYYY")} at{" "}
                {dayjs.utc(workOrderDetail.orderStartTime).format("h:mm A")}
                <br />({getTimeZoneAbbreviation(workOrderDetail.timeZone)})
              </p>
            ) : (
              <p>-</p>
            )}
          </div>
          <div>
            <h6>Labour</h6>
            {fields
              .filter((field) =>
                isRateShown(field, currentRates?.[field.name], newPayment?.[field.name]),
              )
              .map(({ name, label, isAmount }, index) => {
                const hasNewValue = newPayment?.[name] !== undefined;
                return (
                  <div key={name} className={`flex items-center mb-2 ${index === 0 ? "mt-4" : ""}`}>
                    <p className="mr-5">{label}</p>
                    <div className="border-t border-dotted border-gray-500 flex-grow"></div>
                    <p className={`ml-5 font-normal ${hasNewValue ? "line-through" : ""}`}>
                      {renderRateValue(isAmount, currentRates?.[name])}
                    </p>
                    {hasNewValue && (
                      <p className="ml-3 font-normal">
                        {renderRateValue(isAmount, newPayment[name])}
                      </p>
                    )}
                  </div>
                );
              })}
          </div>

          <div className="mt-6">
            {expenses?.length > 0 && (
              <>
                <h6>Expenses</h6>
                {expenses?.map((expense, index) => (
                  <div className="flex items-center mt-2 mb-3" key={expense.id ?? `new-${index}`}>
                    <p className="mr-5">{formatExpenseLabel(expense)}</p>
                    <div className="border-t border-dotted border-gray-500  flex-grow"></div>
                    <div className="flex items-center bg-light py-1 px-3 rounded-md ml-5">
                      {expense.isApproved ? (
                        <i className="fa-solid fa-check"></i>
                      ) : (
                        <i className="fa-solid fa-xmark"></i>
                      )}
                      <p className="ml-2 font-normal">
                        {customerCurrency} {fmt(expense?.amount)}
                      </p>
                    </div>
                    {!expense.isApproved && (
                      <i
                        className="fa-regular fa-trash-can text-red-500 text-base ml-2 cursor-pointer"
                        onClick={() => handleDeleteExpenseButton(index)}
                      ></i>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="mt-6">
            <div className="flex justify-between bg-secondary px-4 py-1 rounded-md">
              <h6 className="!text-white">Estimated Payment</h6>
              <div className="flex">
                <h6 className={`!text-white ${isEdited ? "line-through mr-6" : ""}`}>
                  {customerCurrency} {fmt(currentTotal)}
                </h6>
                {isEdited && (
                  <h6 className="!text-white">
                    {customerCurrency} {fmt(totalNewWorkOrderCost)}
                  </h6>
                )}
              </div>
            </div>

            <p className="mt-3 text-secondary xl:pr-28 text-justify">
              <span className="font-normal">Please note:</span> This is the minimum payable amount;
              however, the final payment will be determined upon job completion based on the actual
              time worked.
            </p>
          </div>
        </BaseCard>

        <BaseCard className="p-4 mt-6" elevation={0}>
          <div>
            <div className="flex items-center gap-2">
              <BaseCheckbox onChange={(e) => setIsLabourOpen(e.target.checked)} />
              <h6 className="text-base font-normal">Labour</h6>
            </div>

            {isLabourOpen && (
              <div>
                <div className="grid grid-cols-7 xl:gap-10 gap-4 items-center">
                  <div className="xl:col-span-6 col-span-7 grid grid-cols-4 xl:gap-10 gap-4 items-end xl:mb-2">
                    <div className="xl:col-span-1 col-span-4">
                      <BaseTextField
                        value={PAYMENT_TYPE_LABELS[paymentType] || "-"}
                        isDefaultField={false}
                        disabled={true}
                      />
                    </div>
                    {fields.map(({ name, label }) => (
                      <div className="xl:col-span-1 col-span-4" key={name}>
                        <BaseTextField
                          isDefaultField={false}
                          label={label}
                          labelShrink={false}
                          // The columns are narrow and "Non-Business Hour Rate" only just
                          // fits: wrap it rather than let MUI clip it to "...Hour Rat".
                          // The grid bottom-aligns its cells, so the inputs stay level.
                          labelProps={{
                            sx: { whiteSpace: "normal", overflow: "visible", flexWrap: "wrap" },
                          }}
                          // No "– Optional" marker: the non-business hour rate may be left
                          // empty (it keeps the current rate), but that is not called out.
                          requirement="none"
                          name={name}
                          value={paymentForm?.[name] ?? ""}
                          onChange={(e) => handleLabourChange(e)}
                          type="float"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="xl:col-span-1 col-span-7">
                    <BaseButton
                      variant="outlined"
                      color="primary"
                      className="py-[6px] xl:mt-3 hover:bg-primary bg-zinc-200/80"
                      fullWidth={true}
                      onClick={handleAddPay}
                    >
                      Add
                    </BaseButton>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <BaseCheckbox onChange={(e) => setIsExpensesOpen(e.target.checked)} />
              <h6 className="text-base font-normal">Expenses</h6>
            </div>

            {isExpensesOpen && (
              <div className="grid grid-cols-7 xl:gap-10 gap-4 items-center">
                <div className="xl:col-span-6 col-span-7 grid grid-cols-3 xl:gap-10 gap-4 items-end xl:mb-2">
                  <div className="xl:col-span-1 col-span-3">
                    <BaseSelect
                      isDefaultField={false}
                      labelShrink={false}
                      className="py-[5px] mb-1.5"
                      label=""
                      required={true}
                      onChange={(e) => handleExpenseChange(e)}
                      options={[
                        { label: "Travel Expense", value: "TRAVEL" },
                        { label: "Tools Expense", value: "TOOLS" },
                        { label: "Material Expense", value: "MATERIAL" },
                        { label: "All Options", value: "ALL" },
                        { label: "Other", value: "OTHER" },
                      ]}
                      name="expenseType"
                      value={newExpense.expenseType}
                    />
                  </div>
                  <div className="xl:col-span-1 col-span-3">
                    <BaseTextField
                      isDefaultField={false}
                      label="Description"
                      name="description"
                      labelShrink={false}
                      onChange={(e) => handleExpenseChange(e)}
                      optional={true}
                      value={newExpense.description}
                    />
                  </div>
                  <div className="xl:col-span-1 col-span-3">
                    <BaseTextField
                      isDefaultField={false}
                      label="Total Amount"
                      labelShrink={false}
                      optional={false}
                      onChange={(e) => handleExpenseChange(e)}
                      name="amount"
                      type="float"
                      value={newExpense.amount}
                    />
                  </div>
                </div>
                <div className="xl:col-span-1 col-span-7">
                  <BaseButton
                    variant="outlined"
                    color="primary"
                    className="py-[1px] h-10 xl:mt-5 hover:bg-primary bg-zinc-200/80"
                    fullWidth={true}
                    onClick={handleAddExpenseButton}
                  >
                    Add
                  </BaseButton>
                </div>
              </div>
            )}
          </div>
        </BaseCard>
      </div>

      <div
        className="xl:flex grid grid-cols-2 gap-4 xl:justify-center  bg-white py-6 px-4"
        style={{
          boxShadow: "0px 0px 14px 5px rgba(0, 0, 0, 0.07)",
        }}
      >
        <div className="xl:block col-span-2">
          <BaseButton
            fullWidth={true}
            className="py-[4px] mr-8"
            variant="outlined"
            color="primary"
            onClick={() => close(false)}
          >
            Cancel
          </BaseButton>
        </div>
        <div className="xl:block col-span-2">
          <BaseButton
            fullWidth={true}
            loaderSize="26px"
            isLoading={isSendingCounterOffer}
            disabled={!isEdited || isSendingCounterOffer}
            // Wide enough for "Update Counter Offer" on one line.
            className="py-[5px] xl:min-w-40 xl:w-auto xl:px-6 whitespace-nowrap"
            color="primary"
            onClick={handleCounterOfferButton}
          >
            {isEditing ? "Update Counter Offer" : "Send Counter Offer"}
          </BaseButton>
        </div>
      </div>
    </BaseModal>
  );
};

export default CustomerCounterOfferModal;
